import * as React from "react";
import { Button as BaseButton, ButtonSize, Hint, Toast } from "react-ui";
import { NetDownloadIcon } from "@skbkontur/icons";

import { AbsoluteModelPath } from "../../../../../Common/ModelPath/ModelPath";
import { EXCEL_DOWNLOAD_BUTTON_CLICK } from "../../metricsAction";
import { useMetrics } from "../../Internals/Metrics/MetricsProviderContext";
import { ControlValidationProps } from "../../Validations/ValidationProps";
import { FocusManagementObserver } from "../../Services/FocusManagement/FocusManagementObserver";
import { emptyRef } from "../../TypingUtils";
import { tryScrollIntoViewWithFallback } from "../../../Helpers/BrowserHelpers";

export interface DownloadExcelButtonProps extends ControlValidationProps {
    bindingPath?: AbsoluteModelPath;
    pathToTable: string;
    align?: React.CSSProperties["textAlign"];
    disabled?: boolean;
    hint?: React.ReactNode;
    size?: ButtonSize;
    width?: number | string;
    onClick: () => Promise<boolean>;
    "data-tid"?: string;
    text?: string;
    textInLoading?: string;
}

export function DownloadExcelButton(props: DownloadExcelButtonProps): JSX.Element {
    const track = useMetrics();
    const rootRef = React.useRef<HTMLSpanElement>(emptyRef);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);

    async function refreshExcelDownload() {
        Toast.close();
        await handleClick();
    }

    async function handleClick() {
        track(EXCEL_DOWNLOAD_BUTTON_CLICK, { path: props.pathToTable });
        setIsLoading(true);
        const isSuccessResponse = await props.onClick();
        if (!isSuccessResponse) {
            Toast.push("Не удалось скачать файл", {
                label: "Повторить",
                handler: refreshExcelDownload,
            });
        }
        setIsLoading(false);
    }

    const handleFocusRequest = (): void => {
        if (rootRef.current != undefined) {
            tryScrollIntoViewWithFallback(rootRef.current, { block: "center", inline: "center" });
        }
    };

    return (
        <FocusManagementObserver onRequestFocus={handleFocusRequest}>
            <Hint text={props.hint}>
                <span ref={rootRef}>
                    <BaseButton
                        data-tid={props["data-tid"]}
                        align={props.align}
                        disabled={props.disabled}
                        size={props.size}
                        width={props.width}
                        onClick={handleClick}
                        loading={isLoading}
                        icon={<NetDownloadIcon />}>
                        {isLoading ? props.textInLoading : props.text}
                    </BaseButton>
                </span>
            </Hint>
        </FocusManagementObserver>
    );
}
